import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import { dispatcher } from 'dispatcher';
import { useUserSelector } from 'js/store/hooks';
import * as actions from 'js/hooks/auth/store/actions';
import { useAfterLoginAction } from 'js/hooks/auth/hooks/useAfterLoginAction';
import { useFailedLoginAction } from 'js/hooks/auth/hooks/useFailedLoginAction';
import { clearFailedLoginAttempts } from '@interlayer/js/app/hooks/auth/helpers';
import { externalLoginAction, storeRememberMeItem } from 'js/hooks/auth/helpers';
import { useSuccessLoginAction } from 'js/hooks/auth/hooks/useSuccessLoginAction';
import { useGetWaitingMessages } from 'js/hooks/auth/hooks/useGetWaitingMessages';
import { useLoginBySessionToken } from 'js/hooks/auth/hooks/useLoginBySessionToken';
import { useRedirectToAfterLoginURL } from 'js/hooks/auth/hooks/useRedirectToAfterLoginURL';
import { useGetCredentialsLoginHooks } from 'js/hooks/auth/hooks/useGetCredentialsLoginHooks';
import { useAfterCredentialsLoginAction } from '@core/js/hooks/auth/hooks/useAfterCredentialsLoginAction';

export const useContinueLoginWithSessionToken = () => {
    const dispatch = useDispatch();
    const afterLoginAction = useAfterLoginAction();
    const failedLoginAction = useFailedLoginAction();
    const successLoginAction = useSuccessLoginAction();
    const getWaitingMessages = useGetWaitingMessages();
    const loginBySessionToken = useLoginBySessionToken();
    const redirectToAfterLoginURL = useRedirectToAfterLoginURL();
    const getCredentialsLoginHooks = useGetCredentialsLoginHooks();
    const afterCredentialsLoginAction = useAfterCredentialsLoginAction();
    const actionAfterLogin = useUserSelector(prop('actionAfterLogin'));

    return useCallback(async ({
        userId,
        userName,
        sessionToken,
        isAfterLoginWithCredentials,

        // buzz
        inputUserName,
        isProceedLoginInNative,
        rememberMeLogin,

        ...rest
    }) => {
        try {
            if (!isProceedLoginInNative) {
                try {
                    await externalLoginAction({ userId, userName, sessionToken, ...rest });
                } catch (error) {
                    if (error?.data?.isPrevented) {
                        return;
                    }

                    return Promise.reject(error);
                }
            }

            const afterLoginData = await loginBySessionToken(userName, sessionToken);

            if (isAfterLoginWithCredentials) {
                await getCredentialsLoginHooks();
            }

            const afterLoginActionData = await afterLoginAction();
            await successLoginAction({ userName, authType: 'credentials' });

            actionAfterLogin(
                afterLoginActionData?.[0]?.userData,
                afterLoginData?.data.sessionToken,
                afterLoginActionData?.[0]?.playerTags
            );

            if (isAfterLoginWithCredentials) {
                dispatcher.dispatch('auth:credentials:login');

                afterCredentialsLoginAction();
            }

            await getWaitingMessages();

            redirectToAfterLoginURL();

            if (rememberMeLogin) {
                storeRememberMeItem(inputUserName);
            }

            dispatch(actions.setIsLoginInProcess(false));

            clearFailedLoginAttempts(afterLoginActionData?.[0]?.userData);

            return {
                userDetails: afterLoginActionData?.[0]?.userData,
                playerTags: afterLoginActionData?.[0]?.playerTags
            };
        } catch (error) {
            failedLoginAction(error);
            dispatch(actions.setIsLoginInProcess(false));

            throw error;
        }
    }, [
        dispatch,
        afterLoginAction,
        actionAfterLogin,
        failedLoginAction,
        getWaitingMessages,
        successLoginAction,
        loginBySessionToken,
        redirectToAfterLoginURL,
        getCredentialsLoginHooks,
        afterCredentialsLoginAction
    ]);
};
